import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";

// ThatOpen / IFC.js
import * as OBF from "@thatopen/components-front";
import * as THREE from "three";

import { DrawVectorTool, PlanOrigin as Origin } from "@treegram/tools-draw-vector";
import { CameraTool } from "@treegram/tools-camera";
import { ImgLoader } from "@treegram/loaders-img";
import { IfcLoader } from "@treegram/loaders-ifc";
import ViewerError from "@/components/viewer-error";
import ViewerLoading from "@/components/viewer-loading";

import useProjectFilePlanContext from "../context/use-project-file-plan-context.ts";
import { highContrastColors } from "@/utils/colors.ts";
import generateSequenceName from "@/utils/generate-sequence-name.ts";

import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import PlanOrigin from "@/pages/takeoff/context/project-organisation/models/plan-origin.ts";
import useViewer, { createViewerTool, ViewerLoader, ViewerTool } from "@/hooks/viewer/use-viewer.ts";

const ViewerRight: React.FC = () => {
    const { isOverlayActive, urlFileRight: urlFile, fileRight: file, fileDataRight: fileData, isDrawingRight: isDrawing, setIsDrawingRight: setIsDrawing, dispatchLeft, dispatchRight: dispatch } = useProjectFilePlanContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const originsInitializedRef = useRef(false);

    const isPdf = useMemo(() => {
        if (!urlFile) return false;
        try {
            const parsedUrl = new URL(urlFile);
            const link = parsedUrl.searchParams.get("link");
            return link?.toLocaleLowerCase().endsWith(".pdf") ?? false;
        } catch {
            return false;
        }
    }, [urlFile]);

    const isIfc = useMemo(() => {
        if (!urlFile) return false;
        try {
            const parsedUrl = new URL(urlFile);
            const link = parsedUrl.searchParams.get("link");
            return link?.toLocaleLowerCase().endsWith(".ifc") ?? false;
        } catch {
            return false;
        }
    }, [urlFile]);

    useEffect(() => {
        if (urlFile && isOverlayActive && !isPdf && !isIfc) {
            toast.error("Format non supporté.", { autoClose: false });
        }
    }, [urlFile, isOverlayActive, isPdf, isIfc]);

    /* --------- TOOLS CONFIG --------- */
    const tools = useMemo<ViewerTool[]>(() => [
        createViewerTool({
            key: "cameraTool",
            factory: (ctx) => new CameraTool(ctx.viewer),
        }),
        createViewerTool({
            key: "drawVectorTool",
            factory: (ctx) => new DrawVectorTool(ctx.viewer),
        }),
    ], []);

    /* --------- LOADERS CONFIG --------- */
    const planPdfLoader = useMemo<ViewerLoader<unknown, ImgLoader, typeof file>>(() => ({
        key: "plan-pdf-loader",
        factory: (ctx) => new ImgLoader(ctx.viewer.getComponents(), ctx.world),
        itemsToLoad: file != null ? [file] : undefined,
        fetchUrl: () => urlFile,
        name: (item) => item?.name ?? "plan",
        getOptions: () => {
            let loaderOptions = {};
            if (fileData.content instanceof KnownScaleContent) {
                loaderOptions = {
                    format: fileData.content.format,
                    orientation: fileData.content.orientation,
                    scale: fileData.content.scale,
                };
            } else if (fileData.content instanceof UserDefinedContent) {
                loaderOptions = { definedScale: fileData.content.scale };
            }
            return { useCoordinates: false, loaderOptions };
        },
        onItemLoaded: async (ctx, loader) => {
            const cameraTool = ctx.getTool(CameraTool);
            cameraTool?.setNavigationMode("Plan");
            const dimensionsCanvas = loader.getDimensionsCanvas();
            if (dimensionsCanvas) {
                await ctx.viewer?.getCamera().controls.setLookAt(0, Math.max(dimensionsCanvas.height, dimensionsCanvas.width) * 0.9, 0, 0, 0, 0);
            }
        },
        onError: (_ctx, _loader, _item, error) => {
            toast.error(error.message, { autoClose: false });
        },
        enabled: isOverlayActive && isPdf && urlFile != null && file != null,
    }), [file, urlFile, fileData.content, isOverlayActive, isPdf]);

    const ifcLoader = useMemo<ViewerLoader<unknown, IfcLoader, typeof file>>(() => ({
        key: "ifc-loader",
        factory: (ctx) => new IfcLoader(ctx.viewer.getComponents(), ctx.world),
        itemsToLoad: file != null ? [file] : undefined,
        fetchUrl: () => urlFile,
        name: (item) => item?.name ?? "ifc",
        getOptions: (item) => ({
            useCoordinates: true,
            loaderOptions: { sitePosition: item?.sitePosition },
        }),
        onItemLoaded: async (ctx, loader) => {
            const cameraTool = ctx.getTool(CameraTool);
            cameraTool?.setNavigationMode("Orbit");
            const model = loader.getModel();
            if (model) {
                const { position } = model.object;
                await ctx.viewer?.getCamera().controls.setLookAt(
                    position.x + 20,
                    position.y + 20,
                    position.z + 20,
                    position.x,
                    position.y,
                    position.z
                );
            }
        },
        onError: (_ctx, _loader, _item, error) => {
            toast.error(error.message, { autoClose: false });
        },
        enabled: isOverlayActive && isIfc && urlFile != null && file != null,
    }), [file, urlFile, isOverlayActive, isIfc]);

    const viewerLoaders = useMemo<ViewerLoader[]>(
        () => [planPdfLoader, ifcLoader],
        [planPdfLoader, ifcLoader]
    );

    const { isLoading, loadingProgress, getTool, error, retry } = useViewer({
        containerRef,
        forceResize: true,
        viewerLoaders,
        tools,
    });


    // Gestion lorsque le viewer a fini de dessiner un trait
    const drawnEndedCallback = useCallback(async (e: Origin) => {
        const drawVectorTool = getTool(DrawVectorTool);
        if (drawVectorTool == null) return;

        const color = drawVectorTool.getColor();

        await drawVectorTool.deactivate();
        drawVectorTool.onEndedCreation.remove(drawnEndedCallback);
        setIsDrawing(false);

        const origin = new PlanOrigin();
        origin.setName(generateSequenceName('Origine', fileData.origins.length));
        origin.setColor(`#${color.getHexString()}`);
        origin.setPosition(e.position);
        origin.setDirection(e.direction);
        origin.setIsActive(true);

        dispatch({
            type: 'add-origin',
            origin
        });

        dispatchLeft({
            type: 'set-relative-origin',
            originId: origin.id
        });
    }, [getTool, fileData.origins.length, setIsDrawing, dispatch, dispatchLeft]);

    // Gestion passage en mode dessin
    useEffect(() => {
        let cancelled = false;
        const drawVectorTool = getTool(DrawVectorTool);

        if (!isDrawing || !drawVectorTool) {
            return () => {
                cancelled = true;
            };
        }

        const run = async () => {
            if (!drawVectorTool) return;

            await drawVectorTool.activate();
            if (cancelled) return;

            const colorHex = highContrastColors[fileData.origins.length % highContrastColors.length];
            drawVectorTool.setColor(new THREE.Color(colorHex));
            drawVectorTool.draw();

            drawVectorTool.onEndedCreation.add(drawnEndedCallback);
        };

        run();

        // Cleanup en cas de changement de state pendant l’attente
        return () => {
            cancelled = true;
            drawVectorTool?.onEndedCreation.remove(drawnEndedCallback);
        };

    }, [isDrawing, getTool, fileData.origins.length, drawnEndedCallback]);

    // Affichage des origines sur le viewer
    useEffect(() => {
        const drawVectorTool = getTool(DrawVectorTool);
        if (error) {
            originsInitializedRef.current = false;
            return;
        }
        if (!isOverlayActive || isLoading || !drawVectorTool) return;
        if (originsInitializedRef.current) return;

        drawVectorTool.clear();

        const vertorLength = 3;

        fileData.origins.forEach(origin => {
            drawVectorTool.setColor(new THREE.Color(origin.color));
            drawVectorTool.addMeasure(new OBF.Line(
                new THREE.Vector3(origin.position.x, origin.position.z, -origin.position.y),
                new THREE.Vector3(origin.position.x + vertorLength * origin.direction.x, origin.position.z, -origin.position.y - vertorLength * origin.direction.y)
            ));
        });

        originsInitializedRef.current = true;
    }, [isOverlayActive, isLoading, getTool, fileData.origins, error]);

    useEffect(() => {
        originsInitializedRef.current = false;
    }, [urlFile]);

    return <div className="relative w-full h-full" aria-busy={isLoading && !error}>
        <div ref={containerRef} className="w-full h-full" />
        {isLoading && !error && <ViewerLoading progress={loadingProgress} />}
        <ViewerError error={error} retry={retry} />
    </div>;
}

export default ViewerRight;
